import useComponentSize from "@rehooks/component-size"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { PostListItemStyles } from "../posts/constants"
import styles from "./styles.module.scss"
import { isMobile } from "react-device-detect"
import { PlayCircle } from "react-bootstrap-icons"
import clsx from "clsx"
import { useQueryClient } from "react-query"
import { Spinner } from "../shared/Spinner"

// How often to poll while a video attachment is still processing on Mux.
// Posts have no push mechanism (unlike comments, which get an ActionCable
// broadcast from the Mux webhook), so this just re-fetches the post list
// periodically until the video is ready.
const PROCESSING_POLL_INTERVAL_MS = 8000

interface Media {
  filename: string
  url: string | null
  content_type_generic: string
  content_type_specific: string
  poster_url?: string | null
  video_ready?: boolean
  isProcessing?: boolean
  signed_id?: string
}

// poster_url comes from Mux itself, available as soon as the asset's
// playback_id is set - well before the video is actually transcoded/
// playable (see MuxAsset#poster_url) - so this often has a real frame from
// the video, not just a generic placeholder, even while still "processing".
const ProcessingVideoPreview: React.FC<{ media: Media }> = ({ media }) => {
  const posterUrl = media.poster_url
  return (
    <div
      className={styles.processingContainer}
      style={
        posterUrl
          ? {backgroundImage: `url(${posterUrl})`, backgroundSize: "cover", backgroundPosition: "center"}
          : undefined
      }
    >
      <div className={posterUrl ? styles.processingScrim : undefined}>
        <Spinner className={styles.processingSpinner} />
        <div className={styles.processingText}>
          Processing video…{" "}
          <span className={styles.filename}>{media.filename}</span>
          <div className={styles.smallProcessingText}>
            Check back soon
          </div>
        </div>
      </div>
    </div>
  )
}

interface ImageVideoAlbumPreviewProps {
  attachedMedia: Media[]
  onClick?: (e?: React.MouseEvent) => void
}

const ImageVideoAlbumPreview: React.FC<ImageVideoAlbumPreviewProps> = ({
  attachedMedia,
  onClick,
}) => {
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  const ref = useRef<HTMLDivElement>(null)
  const videoPreviewRef = useRef<HTMLVideoElement>(null)
  let size = useComponentSize(ref)

  useLayoutEffect(() => {
    setWidth(size.width)
    if (isMobile) {
      setHeight((width * 9) / 16)
    } else {
      setHeight(width ? (width * 9) / 16 : 0)
    }
  }, [size.width, width])

  const headlineStyle: React.CSSProperties = {
    height: height,
  }

  const playableMedia = attachedMedia.filter(
    (media) => media.content_type_generic === "image" || media.video_ready,
  )
  const processingMedia = attachedMedia
    .filter((media) => media.content_type_generic === "video" && !media.video_ready)
    .map((media) => ({ ...media, isProcessing: true }))
  const allMedia = [...playableMedia, ...processingMedia]

  const queryClient = useQueryClient()
  useEffect(() => {
    if (processingMedia.length === 0) return

    const interval = setInterval(() => {
      queryClient.invalidateQueries(["posts"])
    }, PROCESSING_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [processingMedia.length, queryClient])

  // Get the first media item
  const firstMedia = allMedia[0]

  if (!firstMedia) {
    return null
  }

  const containerClassName = clsx(
    PostListItemStyles.contentDiv,
    "w-full",
    onClick && "cursor-pointer",
    firstMedia.content_type_generic === "video" &&
      firstMedia.video_ready &&
      "flex justify-center items-center relative",
  )

  return (
    <div
      ref={ref}
      style={headlineStyle}
      className={containerClassName}
      onClick={onClick}
    >
      {firstMedia.isProcessing ? (
        <ProcessingVideoPreview media={firstMedia} />
      ) : firstMedia.content_type_generic === "video" ? (
        firstMedia.video_ready ? (
          <>
            <video
              ref={videoPreviewRef}
              className={styles.videoPreview}
              preload="metadata"
              muted
            >
              <source src={firstMedia.url ?? undefined} type="video/mp4" />
            </video>
            <PlayCircle
              className={styles.playCircle}
              size={64}
            />
          </>
        ) : null
      ) : (
        <div className={styles.imgContainer}>
          <img
            src={firstMedia.url ?? undefined}
            alt={firstMedia.filename}
            title={firstMedia.filename}
          />
        </div>
      )}
    </div>
  )
}

export default ImageVideoAlbumPreview

